import { useEffect, useRef } from 'react';

import { useSidebar } from '@/components/ui/sidebar';

/** Below this viewport width the labelled sidebar collapses to its icon rail. */
export const SIDEBAR_LABELLED_MIN_WIDTH = 1100;

const SIDEBAR_COOKIE = 'sidebar_state';

/** Whether the sidebar shows icon and word at this viewport width. */
export function isSidebarLabelledAt(width: number): boolean {
  return width >= SIDEBAR_LABELLED_MIN_WIDTH;
}

const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;

function readSavedChoice(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${SIDEBAR_COOKIE}=`))
    ?.split('=')[1];
}

function restoreSavedChoice(previous: string | undefined): void {
  if (typeof document === 'undefined') return;
  document.cookie =
    previous === undefined
      ? `${SIDEBAR_COOKIE}=; path=/; max-age=0`
      : `${SIDEBAR_COOKIE}=${previous}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
}

/**
 * Keeps the desktop sidebar labelled (icon and word) from 1100 px up and an
 * icon rail below it. On load the narrow case always collapses; the wide case
 * opens only when the person has not chosen either state before. After that it
 * acts when the viewport crosses 1100 px, so a manual toggle is left alone
 * until the window is resized across the line.
 *
 * Mobile (the full-screen overlay) is not touched.
 */
export function useSidebarViewportCollapse(): void {
  const { setOpen, isMobile } = useSidebar();
  // `setOpen` changes identity whenever the sidebar toggles; reading it through
  // a ref keeps the effect from re-running (and re-collapsing) on every toggle.
  const setOpenRef = useRef(setOpen);
  setOpenRef.current = setOpen;

  // The provider writes the sidebar cookie on every setOpen. A collapse or open
  // made here is the viewport's doing, not the person's choice, so the cookie is
  // put back to what it held before. Only a manual toggle leaves a saved choice.
  const setAutomatically = useRef((value: boolean) => {
    const previous = readSavedChoice();
    setOpenRef.current(value);
    restoreSavedChoice(previous);
  });

  useEffect(() => {
    if (isMobile || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const query = window.matchMedia(`(min-width: ${SIDEBAR_LABELLED_MIN_WIDTH}px)`);
    if (!query.matches) {
      setAutomatically.current(false);
    } else if (readSavedChoice() === undefined) {
      setAutomatically.current(true);
    }
    const onChange = (event: MediaQueryListEvent) => setAutomatically.current(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [isMobile]);
}
